// useSignedUrl.js
import { useState, useEffect } from "react";
import { generateSignedUrl } from "./getPreSignedURL"; // Ensure correct path

class LRUCache {
  constructor(capacity) {
    this.capacity = capacity;
    this.cache = new Map();
  }

  get(key) {
    if (!this.cache.has(key)) return null;

    // Move the accessed item to the end to show it was recently used
    const value = this.cache.get(key);
    this.cache.delete(key);
    this.cache.set(key, value);

    return value;
  }

  set(key, value) {
    // If key already exists, delete it first
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }

    // If cache is at capacity, remove the least recently used item
    if (this.cache.size >= this.capacity) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(key, value);
  }
}

// Cache object to store signed URLs
const signedUrlCache = new LRUCache(400);

const useSignedUrl = (fileName, folder, isLoaderNeeded) => {
  const [imgSrc, setImgSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fileName || !folder) {
      setLoading(false);
      return;
    }

    const cacheKey = `${folder}/${fileName}`;
    const cachedUrl = signedUrlCache.get(cacheKey);

    // If URL is already cached, use it immediately and return early
    if (cachedUrl) {
      setImgSrc(cachedUrl);
      setLoading(false);
      return;
    }

    const fetchSignedUrl = async () => {
      try {
        const signedUrl = await generateSignedUrl(fileName, folder);
        signedUrlCache.set(cacheKey, signedUrl); // Store in cache
        setImgSrc(signedUrl);
      } catch (error) {
        console.error("Failed to fetch signed URL:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSignedUrl();
  }, [fileName, folder]);
  return isLoaderNeeded ? { imgSrc, loading } : imgSrc;
};

export default useSignedUrl;
